import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { EnhancedToolResult, ToolExecutionContext } from '../types';
import { DesignTool, designTargetSchemaFields } from './designTargeting';

export const readHtmlAssetSchema = z.object({ ...designTargetSchemaFields });
export type ReadHtmlAssetArgs = z.infer<typeof readHtmlAssetSchema>;

/**
 * For when the agent does not already have the composition: an edit from a later session, or a long
 * build whose earlier ones fell out of a compacted context.
 */
export class ReadHtmlAssetTool extends DesignTool<typeof readHtmlAssetSchema> {
    protected override readonly schema = readHtmlAssetSchema;
    protected override readonly toolName = TOOL_NAMES.readHtmlAsset;
    protected override readonly toolDescription =
        "Read a motion graphics scene's current composition HTML. Call this before " +
        `${TOOL_NAMES.editHtmlAsset} when you do not already have the document — you are editing a ` +
        'graphic from an earlier session, or an edit failed because your copy had ' +
        'drifted. If you authored the composition in this conversation you already have ' +
        'it; re-reading it costs tokens for nothing.';

    public getToolDisplayName(): string {
        return 'Read design';
    }

    public get statusIcon(): string {
        return 'code';
    }

    public async execute(
        args: ReadHtmlAssetArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const resolved = this.resolveTarget(args.scene_id);
        if ('error' in resolved) {
            return resolved.error;
        }
        const { asset, scene } = resolved;
        if (!asset.html) {
            return {
                result:
                    `Scene ${scene.id} has no composition yet — it is awaiting generation. Plan it ` +
                    `with ${TOOL_NAMES.writePlan} and author it with ${TOOL_NAMES.writeHtmlAsset}.`,
                isSuccess: true,
            };
        }
        return {
            result: `The current composition HTML for scene ${scene.id}:\n\n${asset.html}`,
            isSuccess: true,
        };
    }
}
